import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Expand } from "lucide-react";
import { cn } from "@/lib/utils";
import { PhotoPlaceholder } from "@/components/vehicles/VehicleImage";
import { Lightbox } from "@/components/vehicles/Lightbox";
import { Badges } from "@/components/vehicles/VehicleCard";
import { NavArrow } from "@/components/vehicles/NavArrow";

export const Gallery = ({ v }) => {
  const imgs = v.images;
  const [index, setIndex] = useState(0);
  const [open, setOpen] = useState(false);
  const touchX = useRef(null);
  const title = `${v.year} ${v.make} ${v.model}`;

  if (!imgs.length) {
    return (
      <div className="aspect-[4/3] overflow-hidden rounded-lg" data-testid="vehicle-gallery">
        <PhotoPlaceholder />
      </div>
    );
  }

  const go = (d) => setIndex((x) => (x + d + imgs.length) % imgs.length);
  const onTouchEnd = (e) => {
    const dx = e.changedTouches[0].clientX - (touchX.current ?? 0);
    if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
  };

  return (
    <div data-testid="vehicle-gallery">
      <div
        className="relative aspect-[4/3] overflow-hidden rounded-lg bg-paper"
        onTouchStart={(e) => {
          touchX.current = e.touches[0].clientX;
        }}
        onTouchEnd={onTouchEnd}
      >
        <motion.img
          key={imgs[index]}
          src={imgs[index]}
          alt={`${title} — photo ${index + 1} of ${imgs.length}`}
          initial={{ opacity: 0.35 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4 }}
          className="h-full w-full object-cover"
          data-testid="gallery-main-image"
        />
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="absolute inset-0 cursor-zoom-in"
          aria-label="Open full-screen gallery"
          data-testid="gallery-open-fullscreen"
        />
        <Badges v={v} className="pointer-events-none absolute left-4 top-4" />
        <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
          <span className="rounded-sm bg-ink/85 px-2.5 py-1.5 text-xs font-semibold tabular-nums text-white" data-testid="gallery-counter">
            {index + 1} / {imgs.length}
          </span>
          <div className="flex gap-2">
            <NavArrow dir={-1} onClick={() => go(-1)} testId="gallery-prev-button" />
            <NavArrow dir={1} onClick={() => go(1)} testId="gallery-next-button" />
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="hidden h-11 items-center gap-2 rounded-md bg-white/95 px-3.5 text-sm font-semibold text-ink shadow-md transition-colors hover:bg-ink hover:text-white sm:flex"
              data-testid="gallery-fullscreen-button"
            >
              <Expand className="h-4 w-4" aria-hidden="true" /> Full screen
            </button>
          </div>
        </div>
      </div>
      <div className="mt-3 flex gap-2 overflow-x-auto pb-1 sm:grid sm:grid-cols-6 sm:overflow-visible" data-testid="gallery-thumbnails">
        {imgs.map((src, i) => (
          <button
            key={src}
            type="button"
            onClick={() => setIndex(i)}
            aria-label={`Show photo ${i + 1}`}
            aria-current={i === index}
            className={cn(
              "relative aspect-[4/3] w-24 shrink-0 overflow-hidden rounded-md bg-paper ring-offset-2 transition-[opacity,box-shadow] sm:w-auto",
              i === index ? "ring-2 ring-brand" : "opacity-70 hover:opacity-100",
            )}
            data-testid={`gallery-thumb-${i}`}
          >
            <img src={src} alt="" loading="lazy" decoding="async" className="h-full w-full object-cover" />
          </button>
        ))}
      </div>
      {v.sourceImageCount > imgs.length && (
        <p className="mt-3 text-sm text-steel">
          Showing {imgs.length} of {v.sourceImageCount} photos.{" "}
          <a href={v.autotraderUrl} target="_blank" rel="noopener noreferrer" className="link-underline font-semibold text-ink" data-testid="gallery-all-photos-link">
            See every photo on AutoTrader
          </a>
        </p>
      )}
      <Lightbox open={open} onOpenChange={setOpen} images={imgs} index={index} setIndex={setIndex} title={title} />
    </div>
  );
};
